import { useState, useCallback, useId } from 'react';
import { Check, FlaskConical, Droplets, Thermometer } from 'lucide-react';
import LabBench from '../../LabBench';
import InfoBox from '../../InfoBox';
import { Beaker, HotPlate } from '@/components/lab';
import { saltFormationColors } from '@/config/experiments/chem1131/experiment6.config';

const stepInfoContent: Record<number, { title: string; content: string; whatHappens?: string; observe?: string[]; equation?: string }> = {
  1: {
    title: 'Preparing the Acid Solution',
    content: 'Hydrochloric acid (HCl) is a strong acid that completely dissociates in water. The 0.5 M concentration provides enough H+ ions for the neutralization while being safe to handle.',
    whatHappens: 'You measure 15 mL of 0.5 M HCl into a beaker. The solution appears colorless but contains H+ and Cl- ions.',
    observe: ['Solution is clear and colorless', 'HCl is corrosive - handle carefully', '0.5 M = 0.5 moles per liter'],
    equation: 'HCl(aq) -> H+(aq) + Cl-(aq)'
  },
  2: {
    title: 'Preparing the Base Solution',
    content: 'Sodium hydroxide (NaOH) is a strong base that provides OH- ions. Using equal volumes of equal concentrations ensures stoichiometric amounts for complete neutralization.',
    whatHappens: 'You measure 15 mL of 0.5 M NaOH. This provides equal moles of OH- to react with the H+ from HCl.',
    observe: ['Solution is clear and colorless', 'NaOH is caustic - avoid skin contact', 'Equal moles of acid and base prepared'],
    equation: 'NaOH(aq) -> Na+(aq) + OH-(aq)'
  },
  3: {
    title: 'Mixing the Solutions',
    content: 'Neutralization occurs when H+ ions from the acid combine with OH- ions from the base to form water. This is one of the most important reactions in chemistry!',
    whatHappens: 'When acid and base mix, H+ and OH- ions immediately combine to form water molecules, releasing energy as heat.',
    observe: ['Solutions mix to form clear solution', 'No visible precipitate forms', 'The reaction is instantaneous'],
    equation: 'H+(aq) + OH-(aq) -> H₂O(l)'
  },
  4: {
    title: 'Observing the Temperature Change',
    content: 'Neutralization is an EXOTHERMIC reaction - it releases energy. The heat comes from the formation of stable O-H bonds in water molecules. This energy was stored in the separate H+ and OH- ions.',
    whatHappens: 'The temperature rises as chemical potential energy is converted to thermal energy. The enthalpy change is about -57 kJ/mol.',
    observe: ['Temperature increases from ~25C to ~45C', 'Beaker may feel warm to touch', 'Heat release confirms reaction occurred'],
    equation: 'H+ + OH- -> H₂O + heat (ΔH = -57.3 kJ/mol)'
  },
  5: {
    title: 'Testing the pH',
    content: 'Litmus paper indicates whether a solution is acidic (red) or basic (blue). A neutral solution (pH 7) shows no color change from the original litmus color.',
    whatHappens: 'When equal moles of strong acid and strong base react completely, the resulting solution is neutral with pH = 7.',
    observe: ['Red litmus stays red (no excess base)', 'Blue litmus stays blue (no excess acid)', 'Confirms complete neutralization']
  },
  6: {
    title: 'Evaporating the Water',
    content: 'After neutralization, the solution contains dissolved NaCl (table salt) and water. Evaporating the water leaves behind the solid salt crystals.',
    whatHappens: 'Heating drives off water as steam, increasing the salt concentration until crystals form.',
    observe: ['Water boils at 100C', 'Steam rises from the dish', 'White residue begins to appear'],
    equation: 'Na+(aq) + Cl-(aq) -> NaCl(s) (after evaporation)'
  },
  7: {
    title: 'Observing Salt Crystals',
    content: 'Sodium chloride (NaCl) forms cubic crystals due to its face-centered cubic lattice structure. Each Na+ is surrounded by 6 Cl- ions and vice versa.',
    whatHappens: 'As water evaporates completely, white cubic crystals of NaCl remain in the dish.',
    observe: ['White crystalline solid remains', 'Crystals have cubic shape', 'This is common table salt!'],
    equation: 'HCl + NaOH -> NaCl + H₂O (complete neutralization reaction)'
  }
};

export default function neutralization() {
  const [showInfo, setShowInfo] = useState<number | null>(null);
  const [acidPrepared, setAcidPrepared] = useState(false);
  const [basePrepared, setBasePrepared] = useState(false);
  const [mixing, setMixing] = useState(false);
  const [mixed, setMixed] = useState(false);
  const [temperatureObserved, setTemperatureObserved] = useState(false);
  const [phTested, setPhTested] = useState(false);
  const [evaporating, setEvaporating] = useState(false);
  const [evaporated, setEvaporated] = useState(false);
  const [saltObserved, setSaltObserved] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [temperature, setTemperature] = useState(25);
  const id = useId();

  const completedSteps = [acidPrepared, basePrepared, mixed, temperatureObserved, phTested, evaporated, saltObserved].filter(Boolean).length;

  const handlePrepareAcid = useCallback(() => {
    if (currentStep === 1 && !acidPrepared) {
      setAcidPrepared(true);
      setCurrentStep(2);
    }
  }, [currentStep, acidPrepared]);

  const handlePrepareBase = useCallback(() => {
    if (currentStep === 2 && !basePrepared) {
      setBasePrepared(true);
      setCurrentStep(3);
    }
  }, [currentStep, basePrepared]);

  const handleMix = useCallback(() => {
    if (currentStep === 3 && basePrepared && !mixed) {
      setMixing(true);

      // Simulate exothermic reaction
      let temp = 25;
      const interval = setInterval(() => {
        temp += 2;
        setTemperature(temp);
        if (temp >= 45) {
          clearInterval(interval);
          setMixing(false);
          setMixed(true);
          setCurrentStep(4);
        }
      }, 100);
    }
  }, [currentStep, basePrepared, mixed]);

  const handleObserveTemperature = useCallback(() => {
    if (currentStep === 4 && mixed && !temperatureObserved) {
      setTemperatureObserved(true);
      setCurrentStep(5);
    }
  }, [currentStep, mixed, temperatureObserved]);

  const handleTestPH = useCallback(() => {
    if (currentStep === 5 && temperatureObserved && !phTested) {
      setPhTested(true);
      setCurrentStep(6);
    }
  }, [currentStep, temperatureObserved, phTested]);

  const handleEvaporate = useCallback(() => {
    if (currentStep === 6 && phTested && !evaporating && !evaporated) {
      setEvaporating(true);

      // Simulate evaporation
      let progress = 0;
      const interval = setInterval(() => {
        progress += 0.02;
        setTemperature(80 + Math.random() * 5);
        if (progress >= 1) {
          clearInterval(interval);
          setEvaporating(false);
          setEvaporated(true);
          setCurrentStep(7);
        }
      }, 100);
    }
  }, [currentStep, phTested, evaporating, evaporated]);

  const handleObserveSalt = useCallback(() => {
    if (currentStep === 7 && evaporated && !saltObserved) {
      setSaltObserved(true);
    }
  }, [currentStep, evaporated, saltObserved]);

  return (
    <div className="experiment-layout">
      <div className="steps-panel">
        <div className="steps-header">
          <FlaskConical size={16} />
          <h3>Part B: Neutralization</h3>
        </div>
        <div className="steps-list">
          {[
            { id: 1, title: 'Prepare Acid', hint: 'Pour 15 mL 0.5 M HCl', done: acidPrepared },
            { id: 2, title: 'Prepare Base', hint: 'Pour 15 mL 0.5 M NaOH', done: basePrepared },
            { id: 3, title: 'Mix Solutions', hint: 'Pour acid into base', done: mixed },
            { id: 4, title: 'Observe Temperature', hint: 'Note exothermic reaction', done: temperatureObserved },
            { id: 5, title: 'Test pH', hint: 'Use litmus paper', done: phTested },
            { id: 6, title: 'Evaporate', hint: 'Heat to dryness', done: evaporated },
            { id: 7, title: 'Observe Salt Crystals', hint: 'Note white NaCl', done: saltObserved }
          ].map((step) => (
            <div
              key={step.id}
              className={`step-item ${step.done ? 'completed' : ''} ${currentStep === step.id ? 'active' : ''}`}
            >
              <div className={`step-number ${step.done ? 'done' : ''}`}>
                {step.done ? <Check size={12} /> : step.id}
              </div>
              <div className="step-content">
                <span className="step-title">{step.title}</span>
                {currentStep === step.id && !step.done && (
                  <span className="step-hint">{step.hint}</span>
                )}
              </div>
              {stepInfoContent[step.id] && (
                <button
                  className="step-info-btn"
                  onClick={(e) => { e.stopPropagation(); setShowInfo(step.id); }}
                >
                  ?
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Temperature indicator */}
        {(mixing || mixed) && (
          <div className="temp-indicator">
            <Thermometer size={16} />
            <span>{temperature.toFixed(0)}C</span>
            {mixing && <span className="exo">(Exothermic!)</span>}
          </div>
        )}
      </div>

      <LabBench
        experimentName="Acid-Base Neutralization"
        completedCount={completedSteps}
        totalSteps={7}
        equipment={['0.5 M HCl', '0.5 M NaOH', 'Beakers', 'Hot plate', 'Litmus paper']}
        labMode="guided"
        currentStep={currentStep}
      >
        <div className="reaction-workspace">
          <div className="equipment-row">
            {/* HCl Beaker */}
            <div
              className={`equipment-station ${currentStep === 1 ? 'clickable' : ''}`}
              onClick={handlePrepareAcid}
            >
              <svg viewBox="0 0 100 130" width={90} height={120}>
                <defs>
                  <linearGradient id={`acid-${id}`} x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="rgba(170,200,220,0.7)" />
                    <stop offset="50%" stopColor="rgba(200,230,245,0.15)" />
                    <stop offset="100%" stopColor="rgba(170,200,220,0.6)" />
                  </linearGradient>
                </defs>
                <rect x="20" y="20" width="60" height="80" fill={`url(#acid-${id})`} stroke="#7A9BB8" strokeWidth="2" rx="2" />
                {acidPrepared && (
                  <rect x="24" y="50" width="52" height="48" fill="rgba(255,255,255,0.9)" />
                )}
                <text x="50" y="115" fontSize="9" fill="#E0E0E0" textAnchor="middle">0.5M HCl</text>
                {acidPrepared && <circle cx="75" cy="25" r="6" fill="var(--secondary)" />}
              </svg>
            </div>

            {/* NaOH Beaker */}
            <div
              className={`equipment-station ${currentStep === 2 ? 'clickable' : ''}`}
              onClick={handlePrepareBase}
            >
              <svg viewBox="0 0 100 130" width={90} height={120}>
                <defs>
                  <linearGradient id={`base-${id}`} x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="rgba(170,200,220,0.7)" />
                    <stop offset="50%" stopColor="rgba(200,230,245,0.15)" />
                    <stop offset="100%" stopColor="rgba(170,200,220,0.6)" />
                  </linearGradient>
                </defs>
                <rect x="20" y="20" width="60" height="80" fill={`url(#base-${id})`} stroke="#7A9BB8" strokeWidth="2" rx="2" />
                {basePrepared && (
                  <rect x="24" y="50" width="52" height="48" fill="rgba(255,255,255,0.9)" />
                )}
                <text x="50" y="115" fontSize="9" fill="#E0E0E0" textAnchor="middle">0.5M NaOH</text>
                {basePrepared && <circle cx="75" cy="25" r="6" fill="var(--secondary)" />}
              </svg>
            </div>

            {/* Reaction/Evaporation Vessel */}
            <div
              className={`equipment-station ${(currentStep >= 3 && currentStep <= 7 && !saltObserved) ? 'clickable' : ''}`}
              onClick={() => {
                if (currentStep === 3 && !mixed) handleMix();
                else if (currentStep === 4 && !temperatureObserved) handleObserveTemperature();
                else if (currentStep === 5 && !phTested) handleTestPH();
                else if (currentStep === 6 && !evaporated) handleEvaporate();
                else if (currentStep === 7 && !saltObserved) handleObserveSalt();
              }}
            >
              <svg viewBox="0 0 140 180" width={150} height={190}>
                <defs>
                  <linearGradient id={`dish-${id}`} x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#E8E0D5" />
                    <stop offset="50%" stopColor="#F5F0E8" />
                    <stop offset="100%" stopColor="#E8E0D5" />
                  </linearGradient>
                </defs>

                {/* Evaporating dish */}
                <ellipse cx="70" cy="80" rx="55" ry="18" fill={`url(#dish-${id})`} stroke="#C8C0B5" strokeWidth="2" />
                <path d="M15 80 Q15 55 70 55 Q125 55 125 80" fill="none" stroke="#C8C0B5" strokeWidth="2" />

                {/* Solution (before evaporation) */}
                {mixed && !evaporated && (
                  <ellipse cx="70" cy="72" rx={evaporating ? 45 - (temperature - 80) : 48} ry="12" fill="rgba(255,255,255,0.8)">
                    {evaporating && (
                      <animate attributeName="rx" values="48;30;10;0" dur="5s" fill="freeze" />
                    )}
                  </ellipse>
                )}

                {/* NaCl crystals */}
                {evaporated && (
                  <g>
                    {[...Array(15)].map((_, i) => (
                      <rect
                        key={i}
                        x={45 + (i % 5) * 10 + Math.random() * 3}
                        y={72 + Math.floor(i / 5) * 4}
                        width={4 + Math.random() * 3}
                        height={4 + Math.random() * 3}
                        fill="#FFFFFF"
                        opacity={0.9}
                        transform={`rotate(${Math.random() * 45} ${50 + (i % 5) * 10} ${74})`}
                      />
                    ))}
                  </g>
                )}

                {/* Steam animation */}
                {evaporating && (
                  <g>
                    {[...Array(5)].map((_, i) => (
                      <ellipse
                        key={i}
                        cx={50 + i * 10}
                        cy={45}
                        rx={3}
                        ry={4}
                        fill="rgba(255,255,255,0.4)"
                      >
                        <animate attributeName="cy" values="45;20;45" dur={`${1 + i * 0.2}s`} repeatCount="indefinite" />
                        <animate attributeName="opacity" values="0.4;0.1;0" dur={`${1 + i * 0.2}s`} repeatCount="indefinite" />
                      </ellipse>
                    ))}
                  </g>
                )}

                {/* Heat waves from hot plate */}
                {evaporating && (
                  <g opacity="0.3">
                    {[...Array(3)].map((_, i) => (
                      <path
                        key={i}
                        d={`M${40 + i * 25} 130 Q${45 + i * 25} 120 ${40 + i * 25} 110`}
                        fill="none"
                        stroke="#FF5A36"
                        strokeWidth="2"
                      >
                        <animate attributeName="d"
                          values={`M${40 + i * 25} 130 Q${45 + i * 25} 120 ${40 + i * 25} 110;M${40 + i * 25} 130 Q${35 + i * 25} 120 ${40 + i * 25} 110;M${40 + i * 25} 130 Q${45 + i * 25} 120 ${40 + i * 25} 110`}
                          dur="0.8s"
                          repeatCount="indefinite"
                        />
                      </path>
                    ))}
                  </g>
                )}

                {/* Hot plate */}
                <rect x="20" y="120" width="100" height="40" fill="#3A3A3A" rx="5" />
                <circle cx="70" cy="138" r="25" fill="none" stroke={evaporating ? "#FF4500" : "#4A4A4A"} strokeWidth="2" />
                <circle cx="70" cy="138" r="15" fill="none" stroke={evaporating ? "#FF4500" : "#4A4A4A"} strokeWidth="2" />

                <text x="70" y="175" fontSize="9" fill="#E0E0E0" textAnchor="middle">
                  {evaporated ? 'NaCl Crystals' : evaporating ? 'Evaporating...' : mixed ? 'Heat to Evaporate' : 'Mix Here'}
                </text>
              </svg>
            </div>
          </div>

          {/* Status Messages */}
          <div className="status-area">
            {currentStep === 1 && !acidPrepared && (
              <div className="status-message info">
                <Droplets size={20} />
                Click HCl beaker to prepare acid solution
              </div>
            )}
            {currentStep === 2 && !basePrepared && (
              <div className="status-message info">
                <Droplets size={20} />
                Click NaOH beaker to prepare base solution
              </div>
            )}
            {currentStep === 3 && !mixed && (
              <div className="status-message info">
                <FlaskConical size={20} />
                Click the dish to mix acid and base
              </div>
            )}
            {mixing && (
              <div className="status-message reaction">
                <Thermometer size={20} />
                Exothermic reaction! Temperature rising...
              </div>
            )}
            {currentStep === 4 && mixed && !temperatureObserved && (
              <div className="status-message info">
                <Thermometer size={20} />
                Click to observe the temperature change (exothermic!)
              </div>
            )}
            {currentStep === 5 && !phTested && (
              <div className="status-message info">
                <FlaskConical size={20} />
                Click to test pH with litmus paper - should be neutral
              </div>
            )}
            {currentStep === 6 && !evaporating && !evaporated && (
              <div className="status-message info">
                <FlaskConical size={20} />
                Click to heat and evaporate the water
              </div>
            )}
            {evaporating && (
              <div className="status-message reaction">
                <Thermometer size={20} />
                Evaporating water... NaCl crystals forming
              </div>
            )}
            {currentStep === 7 && evaporated && !saltObserved && (
              <div className="status-message info">
                <Check size={20} />
                Click to observe the white NaCl salt crystals
              </div>
            )}
            {saltObserved && (
              <div className="status-message success">
                <Check size={20} />
                Complete! White NaCl crystals formed by neutralization.
              </div>
            )}
          </div>

          {/* Theory Box */}
          <div className="theory-box">
            <h4>Neutralization Reaction</h4>
            <div className="equation">
              HCl + NaOH → NaCl + H₂O
            </div>
            <p>
              An acid (HCl) reacts with a base (NaOH) to form a salt (NaCl) and water.
              This reaction is <strong>exothermic</strong> - heat is released as the
              H+ and OH- ions combine to form water.
            </p>
          </div>
        </div>

        <style>{`
          .reaction-workspace {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 24px;
            padding: 20px;
            width: 100%;
          }

          .equipment-row {
            display: flex;
            gap: 30px;
            align-items: flex-end;
            justify-content: center;
          }

          .equipment-station {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 8px;
            padding: 10px;
            border-radius: 8px;
            transition: all 0.2s ease;
          }

          .equipment-station.clickable {
            cursor: pointer;
          }

          .equipment-station.clickable:hover {
            background: rgba(0, 168, 107, 0.1);
          }

          .temp-indicator {
            display: flex;
            align-items: center;
            gap: 8px;
            margin-top: 12px;
            padding: 8px 12px;
            background: var(--bg-elevated);
            border-radius: 6px;
            color: #FF5A36;
          }

          .temp-indicator .exo {
            font-size: 11px;
            color: #FBBF24;
          }

          .status-area {
            min-height: 50px;
            display: flex;
            justify-content: center;
          }

          .status-message {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 12px 20px;
            border-radius: 8px;
            font-size: 14px;
            font-weight: 500;
          }

          .status-message.info {
            background: rgba(59, 130, 246, 0.2);
            color: #60A5FA;
          }

          .status-message.reaction {
            background: rgba(255, 90, 54, 0.2);
            color: #FF5A36;
          }

          .status-message.success {
            background: rgba(0, 168, 107, 0.2);
            color: var(--secondary);
          }

          .theory-box {
            background: var(--bg-elevated);
            border-radius: 8px;
            padding: 16px;
            max-width: 500px;
            border-left: 3px solid var(--secondary);
          }

          .theory-box h4 {
            margin: 0 0 8px 0;
            color: var(--secondary);
            font-size: 14px;
          }

          .theory-box .equation {
            font-family: 'Courier New', monospace;
            background: rgba(0, 0, 0, 0.3);
            padding: 8px 12px;
            border-radius: 4px;
            margin-bottom: 8px;
            color: #FFFFFF;
          }

          .theory-box p {
            margin: 0;
            font-size: 13px;
            color: var(--text-secondary);
            line-height: 1.5;
          }

          .theory-box strong {
            color: #FF5A36;
          }
        .step-info-btn {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: var(--accent);
          color: var(--bg-dark);
          font-size: 11px;
          font-weight: 700;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: transform 0.15s;
        }
        .step-info-btn:hover {
          transform: scale(1.1);
        }
        `}</style>
      </LabBench>

      {showInfo && stepInfoContent[showInfo] && (
        <InfoBox
          step={showInfo}
          experimentId="exp6"
          stepInfo={stepInfoContent[showInfo]}
          onClose={() => setShowInfo(null)}
        />
      )}
    </div>
  );
}
